import { getConfig } from './config';

export interface GenerationRequestPayload {
  humanImage?: File;
  petImage?: File;
  humanImages?: File[];
  petImages?: File[];
  mood: string;
  clothingStyle: string;
  numberOfPersons?: number;
  numberOfPets?: number;
  clientId: string;
  rootId?: string;
  accessToken?: string;
  trademark?: string;
}

export interface GenerationPreview {
  id: string;
  accessToken?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  mood: string;
  clothingStyle: string;
  numberOfPersons?: number;
  numberOfPets?: number;
  rootId?: string;
  versions?: GenerationVersion[];
  previewUrl?: string;
  downloadUrl?: string;
  errorMessage?: string;
  versionNumber?: number;
  remainingUses?: number;
}

export interface GenerationVersion {
  id: string;
  accessToken?: string;
  versionNumber: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  previewUrl?: string;
}

export interface PaymentLinkPayload {
  productType: 'instant' | 'refined';
  accessToken?: string;
  variantId?: string;
  personalizedNote?: string;
}

export interface CheckoutPayload {
  url: string;
  checkoutUrl: string;
  variantId: string;
  quantity: number;
  attributes: Record<string, string>;
}

function apiUrl(path: string) {
  const base = getConfig().apiBaseUrl.replace(/\/$/, '');
  return `${base}${path}`;
}

function apiBaseUrl() {
  return getConfig().apiBaseUrl.replace(/\/$/, '');
}

function withAccessToken(path: string, accessToken?: string) {
  if (!accessToken) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}token=${encodeURIComponent(accessToken)}`;
}

function normalizeMediaUrl(url?: string) {
  if (!url) return url;
  const trimmed = url.trim();

  if (/^[a-z0-9.-]+\.[a-z]{2,}(?::\d+)?\/.+/i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.pathname.startsWith('/uploads/')) {
      return `${apiBaseUrl()}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Keep relative or malformed values untouched so the caller can surface the failure.
  }

  return trimmed;
}

function normalizePreview(preview: GenerationPreview): GenerationPreview {
  return {
    ...preview,
    previewUrl: normalizeMediaUrl(preview.previewUrl),
    downloadUrl: normalizeMediaUrl(preview.downloadUrl),
    versions: preview.versions?.map((version) => ({
      ...version,
      previewUrl: normalizeMediaUrl(version.previewUrl)
    }))
  };
}

function apiHeaders(contentType?: string) {
  const headers: Record<string, string> = {};

  if (contentType) headers['Content-Type'] = contentType;

  return headers;
}

async function apiError(response: Response) {
  const body = await response.text();
  try {
    const parsed = JSON.parse(body) as { message?: string | string[]; error?: string };
    if (Array.isArray(parsed.message)) return parsed.message.join(' ');
    if (parsed.message) return parsed.message;
    if (parsed.error) return parsed.error;
  } catch {
    // Fall through to the raw body below.
  }

  return body || 'Something went wrong. Please try again.';
}

export async function createGeneration(
  payload: GenerationRequestPayload
): Promise<{ id: string; rootId?: string; accessToken?: string; remainingUses?: number }> {
  const body = new FormData();
  const humanImages = payload.humanImages?.length ? payload.humanImages : payload.humanImage ? [payload.humanImage] : [];
  const petImages = payload.petImages?.length ? payload.petImages : payload.petImage ? [payload.petImage] : [];
  humanImages.forEach((image) => body.append('humanImages', image));
  petImages.forEach((image) => body.append('petImages', image));
  body.append('mood', payload.mood);
  body.append('clothingStyle', payload.clothingStyle);
  body.append('numberOfPersons', String(payload.numberOfPersons || humanImages.length || 1));
  body.append('numberOfPets', String(payload.numberOfPets || petImages.length || 1));
  body.append('clientId', payload.clientId);
  if (payload.rootId) body.append('rootId', payload.rootId);
  if (payload.accessToken) body.append('accessToken', payload.accessToken);
  if (payload.trademark) body.append('trademark', payload.trademark);

  const response = await fetch(apiUrl('/image-generation/upload-images'), {
    method: 'POST',
    headers: apiHeaders(),
    body
  });

  if (!response.ok) {
    throw new Error(await apiError(response));
  }

  return response.json();
}

export async function getPreview(id: string, accessToken?: string): Promise<GenerationPreview> {
  const response = await fetch(apiUrl(withAccessToken(`/image-generation/preview/${id}`, accessToken)), {
    cache: 'no-store',
    headers: apiHeaders()
  });
  if (!response.ok) {
    throw new Error(await apiError(response));
  }
  return normalizePreview(await response.json());
}

export async function getRemainingUsage(clientId: string): Promise<{ remaining: number; limit: number }> {
  const response = await fetch(apiUrl(`/image-generation/usage/remaining?clientId=${encodeURIComponent(clientId)}`), {
    cache: 'no-store',
    headers: apiHeaders()
  });

  if (!response.ok) {
    throw new Error(await apiError(response));
  }

  return response.json();
}

export async function retryGeneration(
  id: string,
  accessToken: string | undefined,
  clientId: string,
  trademark?: string
): Promise<{ id: string; accessToken?: string; remainingUses: number }> {
  const response = await fetch(apiUrl(`/image-generation/retry/${id}`), {
    method: 'POST',
    headers: apiHeaders('application/json'),
    body: JSON.stringify({ accessToken, clientId, trademark })
  });

  if (!response.ok) {
    throw new Error(await apiError(response));
  }

  return response.json();
}

export async function createPaymentLink(id: string, payload: PaymentLinkPayload): Promise<CheckoutPayload> {
  const response = await fetch(apiUrl(`/image-generation/payment-link/${id}`), {
    method: 'POST',
    headers: apiHeaders('application/json'),
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error(await apiError(response));
  }

  return response.json();
}
